/**
 * Locale Configuration Utilities
 */

export interface LocaleConfig {
  DRlocale: string;
  currency: string;
  apiCurrency: string;
  region: string;
  PromoLocale: string;
}

/**
 * Get locale configuration based on locale string
 * @param locale Locale string (e.g., "en-us", "fr-fr", etc.)
 * @returns LocaleConfig object with locale settings
 */
export function getLocaleConfig(locale: string): LocaleConfig {
  switch (locale) {
    case "en":
      return {
        DRlocale: "en_us",
        currency: "$",
        apiCurrency: "USD",
        region: "us",
        PromoLocale: "en_US"
      };
    case "en-zz":
      return {
        DRlocale: "en_us",
        currency: "$",
        apiCurrency: "USD",
        region: "us",
        PromoLocale: "en_US"
      };
    case "en-ca":
      return {
        DRlocale: "en_us",
        currency: "$",
        apiCurrency: "USD",
        region: "us",
        PromoLocale: "en_US"
      };
    case "en-us":
      return {
        DRlocale: "en_us",
        currency: "$",
        apiCurrency: "USD",
        region: "us",
        PromoLocale: "en_US"
      };
    case "en-eu":
      return {
        DRlocale: "en_eu",
        currency: "€",
        apiCurrency: "EUR",
        region: "eu",
        PromoLocale: "en_IE"
      };
    case "fr-fr":
      return {
        DRlocale: "fr_fr",
        currency: "€",
        apiCurrency: "EUR",
        region: "fr",
        PromoLocale: "fr_FR"
      };
    case "de-de":
      return {
        DRlocale: "de_de",
        currency: "€",
        apiCurrency: "EUR",
        region: "de",
        PromoLocale: "de_DE"
      };
    case "en-gb":
      return {
        DRlocale: "en_gb",
        currency: "£",
        apiCurrency: "GBP",
        region: "gb",
        PromoLocale: "en_GB"
      };
    case "es-es":
      return {
        DRlocale: "es_es",
        currency: "€",
        apiCurrency: "EUR",
        region: "es",
        PromoLocale: "es_ES"
      };
    case "it-it":
      return {
        DRlocale: "it_it",
        currency: "€",
        apiCurrency: "EUR",
        region: "it",
        PromoLocale: "it_IT"
      };
    case "pl-pl":
      return {
        DRlocale: "pl_pl",
        currency: "zł",
        apiCurrency: "PLN",
        region: "pl",
        PromoLocale: "pl_PL"
      };
    case "nb-no":
      return {
        DRlocale: "no_no",
        currency: "kr",
        apiCurrency: "NOK",
        region: "no",
        PromoLocale: "no_NO"
      };
    case "sv-se":
      return {
        DRlocale: "sv_se",
        currency: "kr",
        apiCurrency: "SEK",
        region: "se",
        PromoLocale: "sv_SE"
      };
    case "da-dk":
      return {
        DRlocale: "en_gb",
        currency: "kr",
        apiCurrency: "DKK",
        region: "dk",
        PromoLocale: "da_DK"
      };
    case "fr-be":
      return {
        DRlocale: "fr_fr",
        currency: "€",
        apiCurrency: "EUR",
        region: "be",
        PromoLocale: "fr_BE"
      };
    case "de-at":
      return {
        DRlocale: "de_de",
        currency: "€",
        apiCurrency: "EUR",
        region: "at",
        PromoLocale: "de_AT"
      };
    case "cs-cz":
      return {
        DRlocale: "en_gb",
        currency: "Kč",
        apiCurrency: "CZK",
        region: "cz",
        PromoLocale: "cs_CZ"
      };
    case "fi-fi":
      return {
        DRlocale: "en_gb",
        currency: "€",
        apiCurrency: "EUR",
        region: "fi",
        PromoLocale: "fi_FI"
      };
    case "nl-nl":
      return {
        DRlocale: "nl_nl",
        currency: "€",
        apiCurrency: "EUR",
        region: "nl",
        PromoLocale: "nl_NL"
      };
    case "en-au":
      return {
        DRlocale: "en_au",
        currency: "A$",
        apiCurrency: "AUD",
        region: "au",
        PromoLocale: "en_AU"
      };
    case "zh-cn":
      return {
        DRlocale: "zh_cn",
        currency: "CNY",
        apiCurrency: "CNY",
        region: "cn",
        PromoLocale: "zh_CN"
      };
    case "ja-jp":
      return {
        DRlocale: "ja_jp",
        currency: "JPY",
        apiCurrency: "JPY",
        region: "jp",
        PromoLocale: "ja_JP"
      };
    case "ko-kr":
      return {
        DRlocale: "ko_kr",
        currency: "KRW",
        apiCurrency: "KRW",
        region: "kr",
        PromoLocale: "ko_KR"
      };
    case "zh-tw":
      return {
        DRlocale: "zh_tw",
        currency: "TWD",
        apiCurrency: "TWD",
        region: "tw",
        PromoLocale: "zh_TW"
      };
    case "ru-ru":
      return {
        DRlocale: "ru_ru",
        currency: "руб",
        apiCurrency: "RUB",
        region: "ru",
        PromoLocale: "ru_RU"
      };
    case "en-in":
      return {
        DRlocale: "en_in",
        currency: "Rs.",
        apiCurrency: "INR",
        region: "in",
        PromoLocale: "en_IN"
      };
    case "pt-pt":
      return {
        DRlocale: "en_gb",
        currency: "€",
        apiCurrency: "EUR",
        region: "pt",
        PromoLocale: "en_GB"
      };
    default:
      return {
        DRlocale: "en_us",
        currency: " ",
        apiCurrency: "USD",
        region: "us",
        PromoLocale: "en_US"
      };
  }
}

/**
 * Get all supported locales
 */
export function getSupportedLocales(): string[] {
  return [
    "en",
    "en-zz",
    "en-ca",
    "en-us",
    "en-eu",
    "fr-fr",
    "de-de",
    "en-gb",
    "es-es",
    "it-it",
    "pl-pl",
    "nb-no",
    "sv-se",
    "da-dk",
    "fr-be",
    "de-at",
    "cs-cz",
    "fi-fi",
    "nl-nl",
    "en-au",
    "zh-cn",
    "ja-jp",
    "ko-kr",
    "zh-tw",
    "ru-ru",
    "en-in",
    "pt-pt"
  ];
}

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale: string): boolean {
  return getSupportedLocales().includes(locale);
}

